import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { generatePath, useHistory } from 'react-router-dom'

import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import ToggleButton from 'common/ToggleButton/ToggleButton'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useRootStore } from 'stores/RootStore'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useTogglePostApproval } from 'hooks/useTogglePostApproval'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { APPROVALS_PATH } from 'paths'

import Styles from './ApprovalsSettings.module.scss'

export const ApprovalsSettings = observer(() => {
  const workspace = useCurrentWorkspace()
  const history = useHistory()
  const { currentSubscription } = useRootStore().tierUsageStore
  const isPostApprovalsEnabled = useFlipperFlag('post_approvals')
  const togglePostApproval = useTogglePostApproval()

  const isGrowthPlan = currentSubscription?.plan === PaymentPlanKey.BlazeAutopilotGrowth

  const shouldShowUpgradeModal = useMemo(() => {
    if (!currentSubscription) return true
    const isCanceled = currentSubscription.status === 'canceled'
    const isStarterPlan = currentSubscription.plan === PaymentPlanKey.BlazeAutopilotStarter
    return isStarterPlan || isCanceled
  }, [currentSubscription])

  const handleToggle = () => {
    if (!workspace) return

    if (shouldShowUpgradeModal) {
      history.push(generatePath(APPROVALS_PATH, { workspaceId: workspace.id }))
      return
    }

    togglePostApproval()
  }

  if (!isPostApprovalsEnabled || !workspace?.is_virtual_marketer) return null

  const listItems = [
    'Nothing posts without your approval',
    'Easily make quick revisions',
    'Move content to Draft to revisit later',
  ]
  const isToggledChecked = !shouldShowUpgradeModal && !!workspace.post_approval_enabled

  return (
    <div className={Styles.section}>
      <div className={Styles.headerRow}>
        <div className={Styles.headerContent}>
          <Heading level={3}>Approvals</Heading>
          <Text variant="secondary" color="var(--dark-60)" className={Styles.description}>
            Require your review on all of your content before it automatically publishes
          </Text>
          {!isToggledChecked && (
            <ul className={Styles.bulletList}>
              {listItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
        <div className={Styles.toggleRow}>
          {!isToggledChecked && !isGrowthPlan && (
            <Text variant="secondary" className={Styles.toggleLabel}>
              Open Approvals Page
            </Text>
          )}
          <ToggleButton checked={isToggledChecked} name="approvals-toggle" handleChange={handleToggle} size="sm-md" />
        </div>
      </div>
    </div>
  )
})
